/**
 * useMaxQuantKline — 直接连接 MaxQuant WebSocket 获取实时 K 线
 *
 * 协议参考 price-wss.md：
 *   1m: wss://stg-ws.maxquant.ai/ws
 *   5m: wss://stg-ws.maxquant.ai/ws_5m
 *   1s: wss://stg-ws.maxquant.ai/ws_1s
 *
 * 仅消费当前 UI 需要的字段：
 *   - snapshot.data[symbol].points → 价格折线
 *   - update.point → 增量追加最新一根
 * 趋势/反转点字段已在协议中提供，但 UI 不展示，故不解析。
 *
 * 连接策略：每个 interval 一条 WSS（URL 不同），可由上层用 `enabled` 按需在多条连接间并行
 *（例如多卡片各自选 1s/1m/5m 时，1m 与其它周期可同时保持）。也支持只开一条
 * `enabled: true` + 单 interval。自动重连 + 首包超时 + 手动 reconnect。
 * 应用层 ping：若服务端以后要求保活，在 onopen 里按 price-wss 约定补发即可；当前未实现。
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";

export type MaxQuantInterval = "1s" | "1m" | "5m";

export type TrendDirection = "long" | "short" | "neutral";

export interface KlinePoint {
  t: number;
  p: number;
}

export interface KlineTrend {
  /** "long" | "short" | "neutral" */
  current: TrendDirection;
  /** 1=long, -1=short, 0=neutral */
  regime: number;
  /** 趋势开始时间戳（毫秒），update 消息里通常没有 */
  since_t?: number;
}

export interface KlineReversal {
  t: number;
  from: string;
  to: string;
  p: number;
  i: number;
}

export interface KlineSnapshot {
  symbol: string;
  interval: MaxQuantInterval;
  window: number;
  points: KlinePoint[];
  reversals: KlineReversal[];
  /** 当前趋势（snapshot/update/resync 都会更新） */
  trend: KlineTrend | null;
  /** 1s 流特有：当前分钟开始时间戳（毫秒） */
  minute_t?: number;
  /** 1s 流特有：当前分钟内涨跌百分比（小数） */
  chg_in_min?: number;
  lastUpdate: number;
  connected: boolean;
}

export type KlineSnapshotMap = Record<string, KlineSnapshot>;

const SYMBOLS = ["BTCUSDT", "ETHUSDT", "BNBUSDT", "SOLUSDT"] as const;
const LIMIT_BY_INTERVAL: Record<MaxQuantInterval, number> = {
  "1s": 600,
  "1m": 120,
  "5m": 160,
};

const WINDOW_BY_INTERVAL: Record<MaxQuantInterval, number> = {
  "1s": LIMIT_BY_INTERVAL["1s"],
  "1m": LIMIT_BY_INTERVAL["1m"],
  "5m": LIMIT_BY_INTERVAL["5m"],
};

const PATH_BY_INTERVAL: Record<MaxQuantInterval, string> = {
  "1s": "/ws_1s",
  "1m": "/ws",
  "5m": "/ws_5m",
};

const WS_HOST = "wss://stg-ws.maxquant.ai";

/** 已连上 WSS 但久未收到任意币种首包 snapshot/update 的判定时间（与「单连接+换周期」策略配套） */
const SNAPSHOT_WAIT_MS = 20_000;

function anySymbolHasPoints(map: KlineSnapshotMap): boolean {
  for (const s of SYMBOLS) {
    if ((map[s]?.points?.length ?? 0) > 0) return true;
  }
  return false;
}

export interface UseMaxQuantKlineResult {
  snapshots: KlineSnapshotMap;
  /** WebSocket 是否处于 onopen 状态 */
  wsConnected: boolean;
  lastError: string | null;
  /**
   * 在 ws 已开的情况下，超时仍无任何 symbol 的 points 数据
   * （用于与 lastError 区分：后者多为瞬时 onerror，会自动重连）
   */
  awaitSnapshotTimedOut: boolean;
  /** 主动断开并重连（同周期或换周期后仍无数据时可用） */
  reconnect: () => void;
}

export interface UseMaxQuantKlineOptions {
  /**
   * 为 false 时不建立 WSS，snapshot 为对应周期的空表（用于多卡片并行：仅对「有卡片选中的周期」建连）。
   * 默认 true。
   */
  enabled?: boolean;
}

export function useMaxQuantKline(
  interval: MaxQuantInterval,
  options?: UseMaxQuantKlineOptions
): UseMaxQuantKlineResult {
  const enabled = options?.enabled !== false;

  const [snapshots, setSnapshots] = useState<KlineSnapshotMap>(() =>
    initEmpty(interval)
  );
  const [wsConnected, setWsConnected] = useState(false);
  const [lastError, setLastError] = useState<string | null>(null);
  const [awaitSnapshotTimedOut, setAwaitSnapshotTimedOut] = useState(false);
  const [reconnectNonce, setReconnectNonce] = useState(0);

  const wsRef = useRef<WebSocket | null>(null);
  /** 每建立/销毁一条连接自增，用于忽略旧 socket 的 onmessage/onclose，避免多路并存 */
  const linkGenRef = useRef(0);
  const reconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const snapshotWaitTimerRef = useRef<ReturnType<typeof setTimeout> | null>(
    null
  );
  const closedByUserRef = useRef(false);

  const clearSnapshotWait = useCallback(() => {
    if (snapshotWaitTimerRef.current) {
      clearTimeout(snapshotWaitTimerRef.current);
      snapshotWaitTimerRef.current = null;
    }
  }, []);

  const snapshotsRef = useRef(snapshots);
  snapshotsRef.current = snapshots;

  const startSnapshotWait = useCallback(() => {
    clearSnapshotWait();
    setAwaitSnapshotTimedOut(false);
    snapshotWaitTimerRef.current = setTimeout(() => {
      snapshotWaitTimerRef.current = null;
      if (anySymbolHasPoints(snapshotsRef.current)) return;
      setAwaitSnapshotTimedOut(true);
    }, SNAPSHOT_WAIT_MS);
  }, [clearSnapshotWait]);

  const reconnect = useCallback(() => {
    if (!enabled) return;
    setLastError(null);
    setAwaitSnapshotTimedOut(false);
    clearSnapshotWait();
    setReconnectNonce(n => n + 1);
  }, [clearSnapshotWait, enabled]);

  useEffect(() => {
    if (!enabled) {
      closedByUserRef.current = true;
      linkGenRef.current += 1;
      clearSnapshotWait();
      if (reconnectTimerRef.current) {
        clearTimeout(reconnectTimerRef.current);
        reconnectTimerRef.current = null;
      }
      if (wsRef.current) {
        const old = wsRef.current;
        wsRef.current = null;
        try {
          old.onopen = null;
          old.onclose = null;
          old.onmessage = null;
          old.onerror = null;
          old.close();
        } catch {
          // noop
        }
      }
      setSnapshots(initEmpty(interval));
      setWsConnected(false);
      setLastError(null);
      setAwaitSnapshotTimedOut(false);
      return;
    }

    closedByUserRef.current = false;
    setSnapshots(initEmpty(interval));
    setWsConnected(false);
    setLastError(null);
    setAwaitSnapshotTimedOut(false);
    clearSnapshotWait();

    let attempt = 0;

    const disposeCurrentSocket = () => {
      if (reconnectTimerRef.current) {
        clearTimeout(reconnectTimerRef.current);
        reconnectTimerRef.current = null;
      }
      if (wsRef.current) {
        const old = wsRef.current;
        wsRef.current = null;
        try {
          old.onopen = null;
          old.onclose = null;
          old.onmessage = null;
          old.onerror = null;
          old.close();
        } catch {
          // noop
        }
      }
    };

    const connect = () => {
      // 新连接前必须关掉上一条，否则切周期/重连会堆多个 WSS 同时收包
      disposeCurrentSocket();

      linkGenRef.current += 1;
      const gen = linkGenRef.current;

      const url = `${WS_HOST}${PATH_BY_INTERVAL[interval]}`;
      const ws = new WebSocket(url);
      wsRef.current = ws;

      const isCurrent = () => gen === linkGenRef.current;

      ws.onopen = () => {
        if (!isCurrent()) return;
        attempt = 0;
        setWsConnected(true);
        setLastError(null);
        setAwaitSnapshotTimedOut(false);
        startSnapshotWait();
      };

      ws.onclose = () => {
        if (!isCurrent()) return;
        setWsConnected(false);
        clearSnapshotWait();
        if (closedByUserRef.current) return;
        attempt += 1;
        const delay = Math.min(15000, 800 * 2 ** Math.min(attempt, 4));
        reconnectTimerRef.current = setTimeout(connect, delay);
      };

      ws.onerror = () => {
        if (!isCurrent()) return;
        setLastError("WebSocket 连接异常");
      };

      ws.onmessage = ev => {
        if (!isCurrent()) return;
        try {
          const msg = JSON.parse(ev.data) as MaxQuantMessage;
          handleMessage(msg, interval, setSnapshots);
        } catch {
          // ignore malformed payload
        }
      };
    };

    connect();

    return () => {
      closedByUserRef.current = true;
      linkGenRef.current += 1;
      clearSnapshotWait();
      disposeCurrentSocket();
    };
  }, [enabled, interval, reconnectNonce, clearSnapshotWait, startSnapshotWait]);

  /* 任一侧收到首包 points 后结束「等 snapshot」计时，避免与定时器竞态 */
  useEffect(() => {
    if (anySymbolHasPoints(snapshots)) {
      clearSnapshotWait();
      setAwaitSnapshotTimedOut(false);
    }
  }, [snapshots, clearSnapshotWait]);

  return useMemo(
    () => ({
      snapshots,
      wsConnected,
      lastError,
      awaitSnapshotTimedOut,
      reconnect,
    }),
    [snapshots, wsConnected, lastError, awaitSnapshotTimedOut, reconnect]
  );
}

/* ─────────────── Internals ─────────────── */

type MaxQuantMessage =
  | {
      type: "ready";
      symbols: string[];
      interval: MaxQuantInterval;
      window: number;
    }
  | {
      type: "snapshot";
      interval: MaxQuantInterval;
      window: number;
      symbols: string[];
      data: Record<
        string,
        {
          points: KlinePoint[];
          trend?: KlineTrend;
          reversals?: KlineReversal[];
        }
      >;
    }
  | {
      type: "update";
      symbol: string;
      interval: MaxQuantInterval;
      point: KlinePoint;
      trend?: KlineTrend;
      new_reversal?: KlineReversal | null;
      /** 1s 流特有 */
      minute_t?: number;
      /** 1s 流特有 */
      chg_in_min?: number;
    }
  | {
      type: "resync";
      symbol: string;
      interval: MaxQuantInterval;
      window: number;
      points: KlinePoint[];
      trend?: KlineTrend;
      reversals?: KlineReversal[];
    }
  | { type: string; [k: string]: unknown };

function initEmpty(interval: MaxQuantInterval): KlineSnapshotMap {
  const win = LIMIT_BY_INTERVAL[interval];
  const out: KlineSnapshotMap = {};
  for (const s of SYMBOLS) {
    out[s] = {
      symbol: s,
      interval,
      window: win,
      points: [],
      reversals: [],
      trend: null,
      minute_t: undefined,
      chg_in_min: undefined,
      lastUpdate: 0,
      connected: false,
    };
  }
  return out;
}

function handleMessage(
  msg: MaxQuantMessage,
  expectedInterval: MaxQuantInterval,
  setSnapshots: React.Dispatch<React.SetStateAction<KlineSnapshotMap>>
) {
  if (!msg || typeof msg !== "object") return;

  switch (msg.type) {
    case "snapshot": {
      const {
        data,
        window: win,
        interval,
      } = msg as Extract<MaxQuantMessage, { type: "snapshot" }>;
      if (interval && interval !== expectedInterval) return;
      setSnapshots(prev => {
        const next: KlineSnapshotMap = { ...prev };
        for (const sym of Object.keys(data)) {
          const item = data[sym];
          if (!item) continue;
          next[sym] = {
            symbol: sym,
            interval: expectedInterval,
            window: LIMIT_BY_INTERVAL[expectedInterval],
            points: Array.isArray(item.points)
              ? item.points.slice(-LIMIT_BY_INTERVAL[expectedInterval])
              : [],
            reversals: Array.isArray(item.reversals)
              ? item.reversals.slice()
              : [],
            trend: normalizeTrend(item.trend) ?? null,
            minute_t: undefined,
            chg_in_min: undefined,
            lastUpdate: Date.now(),
            connected: true,
          };
        }
        return next;
      });
      return;
    }

    case "update": {
      const {
        symbol,
        point,
        interval,
        trend,
        new_reversal,
        minute_t,
        chg_in_min,
      } = msg as Extract<MaxQuantMessage, { type: "update" }>;
      if (interval && interval !== expectedInterval) return;
      if (!symbol || !point) return;
      setSnapshots(prev => {
        const cur = prev[symbol];
        if (!cur) return prev;
        const win = LIMIT_BY_INTERVAL[expectedInterval];
        const points = cur.points.slice();
        const last = points[points.length - 1];
        if (last && last.t === point.t) {
          points[points.length - 1] = point;
        } else {
          points.push(point);
          if (points.length > win) points.splice(0, points.length - win);
        }
        const reversals = new_reversal
          ? [...cur.reversals, new_reversal].slice(-32)
          : cur.reversals;
        // update 消息里的 trend 一般不带 since_t，与历史 since_t 合并保留
        const nextTrend = normalizeTrend(trend);
        const mergedTrend: KlineTrend | null = nextTrend
          ? {
              ...nextTrend,
              since_t: nextTrend.since_t ?? cur.trend?.since_t,
            }
          : cur.trend;
        return {
          ...prev,
          [symbol]: {
            ...cur,
            points,
            reversals,
            trend: mergedTrend,
            minute_t: minute_t ?? cur.minute_t,
            chg_in_min: chg_in_min ?? cur.chg_in_min,
            lastUpdate: Date.now(),
            connected: true,
          },
        };
      });
      return;
    }

    case "resync": {
      const {
        symbol,
        points,
        reversals,
        trend,
        window: win,
        interval,
      } = msg as Extract<MaxQuantMessage, { type: "resync" }>;
      if (interval && interval !== expectedInterval) return;
      if (!symbol) return;
      setSnapshots(prev => {
        const cur = prev[symbol] ?? {
          symbol,
          interval: expectedInterval,
          window: LIMIT_BY_INTERVAL[expectedInterval],
          points: [],
          reversals: [],
          trend: null,
          minute_t: undefined,
          chg_in_min: undefined,
          lastUpdate: 0,
          connected: false,
        };
        return {
          ...prev,
          [symbol]: {
            ...cur,
            window: LIMIT_BY_INTERVAL[expectedInterval],
            points: Array.isArray(points)
              ? points.slice(-LIMIT_BY_INTERVAL[expectedInterval])
              : cur.points,
            reversals: Array.isArray(reversals)
              ? reversals.slice()
              : cur.reversals,
            trend: normalizeTrend(trend) ?? cur.trend,
            lastUpdate: Date.now(),
            connected: true,
          },
        };
      });
      return;
    }

    case "ready":
    default:
      return;
  }
}

function normalizeTrend(raw: unknown): KlineTrend | undefined {
  if (!raw || typeof raw !== "object") return undefined;
  const t = raw as Partial<KlineTrend> & Record<string, unknown>;
  const current = t.current;
  if (current !== "long" && current !== "short" && current !== "neutral") {
    return undefined;
  }
  const regime =
    typeof t.regime === "number"
      ? t.regime
      : current === "long"
        ? 1
        : current === "short"
          ? -1
          : 0;
  const since_t = typeof t.since_t === "number" ? t.since_t : undefined;
  return { current, regime, since_t };
}
